/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle
} from 'react';
import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity} from '@ion/core/src/types/entities';
import {FormItem} from '../../components/forms/FormItem';
import {
  useFormSave
} from '../../hooks/useFormSave';
import {
  FormState,
  useFormState
} from '../../hooks/useFormState';
import {SearchableAccountDropdown} from '../SearchableAccountDropdown';
import EmbeddedForm from './EmbeddedForm';
import ForceMoveEnablement, {isValidMoveEntity, MoveDetailFields} from './ForceMoveEnablement';

export interface MoveDetailsFormProps {
  setTaskNickNameAction: (action: string) => void;
  onSave: (details: MoveDetails) => void;
  entity?: BulkActionEntity;
}

export type MoveDetails = {
  toAccount: number;
  force?: boolean;
}

/**
 * Re-usable form with the details for moving an entity.
 */
export const MoveDetailsForm = forwardRef(({
  setTaskNickNameAction,
  onSave,
  entity
}: MoveDetailsFormProps, ref) => {
  const {account}                 = useAuthenticatedUser();
  const [formState, setFormState] = useFormState({
    [MoveDetailFields.TARGET_ACCOUNT] : {
      name     : MoveDetailFields.TARGET_ACCOUNT,
      required : true
    },
    entity : {name: entity}
  });

  const onSubmit = useFormSave({
    formState,
    serialize,
    setFormState,
    onSave
  });

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const onMoveDetailsChange = useCallback((value) => {
    setFormState({
      [MoveDetailFields.TARGET_ACCOUNT] : {
        name     : MoveDetailFields.TARGET_ACCOUNT,
        required : true,
        value
      }
    });
    const to           = translate('common:prepositions.to');
    const accountLabel = translate('common:entityLabels.account');
    const accountValue = value?.title.trim();
    accountValue && setTaskNickNameAction(`${to} ${accountLabel} ${accountValue}`);
  }, []);

  return (
    <EmbeddedForm>
      <FormItem fullWidth>
        <SearchableAccountDropdown required
          loggedInAccount={account}
          onChange={onMoveDetailsChange}
          hasError={formState[MoveDetailFields.TARGET_ACCOUNT]?.error}
          errorText={translate('common:editors.shared.required')} />
      </FormItem>
      <ForceMoveEnablement entity={entity}
        formState={formState}
        onChange={setFormState} />
    </EmbeddedForm>
  );
});

const serialize = (formState: FormState): MoveDetails => {
  const serialized ={toAccount: formState[MoveDetailFields.TARGET_ACCOUNT]?.value?.id};
  if (isValidMoveEntity(formState.entity.name)) {
    serialized.force = formState[MoveDetailFields.ENABLE_FORCE_MOVE]?.value;
  }
  return serialized;
};

export default memo(MoveDetailsForm);
